import { noteFromFrequency } from './noteHelper';
import { getOptions } from './optionsHelper';

/*
human ear sound between 20/20KHertz
voice frequency: 85 to 180 Hz

Note frequencies (https://en.wikipedia.org/wiki/Piano_key_frequencies):
E2 -> 82.40689 (piu' giu' di questa i microfoni da computer non pescano piu' nulla)
C3 -> 130.813
** frequenze target
A3 -> 220.0000
A4 -> 440.0000
A5 -> 880.0000
** end frequenze target
C8 -> 4186.009

scrivere appunti su
- durata delle note e ogni quanto dobbiamo acquisire
- pitch detection

ACQUISIZIONE:
audioContext.sampleRate: 44100Khz -> frequenza tipica di acquisizione (due volte la frequenza
                                     del suono piu' alto udibile - cfr. Nyquist–Shannon)
analyser.fftSize è il numero di campioni che abbiamo in un buffer (default 2048)
frequencyBinCount = fftSize / 2

Quindi il tempo che visualizziamo per ogni buffer è binCount/44100 (col default circa 0.023 secondi)
Se acquisiamo 30fps ( un frame ogni 0.033 ) abbiamo una sovrapposizione. Per non averne possiamo stare sui 21fps.

TEMPO NOTE:
semibreve -> 4/4
minima -> 2/4
semiminima -> 1/4
croma  -> 1/8
semicroma -> 1/16

in una battuta c'è un numero di bpm che dipende dallo spartito. ( 2/4, 4/4, etc... )
bpm? => tempo del battito => tempo delle note.

TODO:
- mic response: si puo' tarare la risposta del microfono alle varie frequenze per bilanciarle.
 */

// params:
// onMicrophoneStateChanged: null,
// onWaveChanged:null,
// onNoteChanged:null

class PitchDetectionController {
  constructor(params) {
    this.params = params;
    this.config = getOptions();
    //
    this.micSourceEnabled = false;
    this.audioContext = null;
    this.micSource = null;
    //
    this.micStream = null;
    this.analyser = null;
    this.highPassFilter = null;
    this.lowPassFilter = null;

    this.dataArray = null;
    this.freqArray = null;
    this.rafID = null;
    this.lastNote = null;
    // statistics
    this.lastFrameTime = null;
    this.framePerSecondsCount = 0;
    this.currentFrameCount = 0;
  }

  optionChanged = () => {
    //reload options
    this.config = getOptions();
    // if mic => stopmic, startmic.
  };

  async getMicrophone() {
    if (this.micSourceEnabled) {
      return;
    }
    // https://stackoverflow.com/questions/50281568/audiocontext-not-allowed-to-start-in-tonejs-chrome
    if (!this.audioContext) this.audioContext = new (window.AudioContext || window.webkitAudioContext)();
    const micMediastream = await navigator.mediaDevices.getUserMedia({
      audio: {
        mandatory: {
          googEchoCancellation: this.config.echoCancellation,
          googAutoGainControl: this.config.autoGainControl,
          googNoiseSuppression: this.config.noiseSuppression,
          googHighpassFilter: this.config.highpassFilter
        },
        optional: []
      },
      video: false
    });
    this.micSource = this.audioContext.createMediaStreamSource(micMediastream);

    this.micStream = micMediastream;
    this.setupAnalyzer(this.micSource);
    this.micSourceEnabled = true;
    if (this.params.onMicrophoneStateChanged) this.params.onMicrophoneStateChanged(this.micSourceEnabled);
  }

  stopMicrophone() {
    if (!this.micSourceEnabled) {
      return;
    }
    if (!window.cancelAnimationFrame) window.cancelAnimationFrame = window.webkitCancelAnimationFrame;
    window.cancelAnimationFrame(this.rafID);

    this.teardownAnalyzer();
    if (this.micSource) {
      this.micSource.disconnect();
      this.micStream.getTracks().forEach(track => track.stop());
      this.micSource = null;
    }
    this.micSourceEnabled = false;
    if (this.params.onMicrophoneStateChanged) this.params.onMicrophoneStateChanged(this.micSourceEnabled);
  }

  toggleMicrophone = () => {
    if (this.micSourceEnabled) {
      this.stopMicrophone();
    } else {
      this.getMicrophone();
    }
  };

  getOscillator(frequency, intensity) {
    if (this.micSourceEnabled) this.stopMicrophone();

    if (!this.audioContext) this.audioContext = new (window.AudioContext || window.webkitAudioContext)();
    this.oscillatorNode = this.audioContext.createOscillator();
    this.oscillatorNode.frequency.value = frequency === null ? 440 : frequency; // The A above middle C
    this.setupAnalyzer(this.oscillatorNode);
    this.oscillatorNode.start();
    this.micSourceEnabled = true;
    if (this.params.onMicrophoneStateChanged) this.params.onMicrophoneStateChanged(this.micSourceEnabled);
  }

  stopOscillator() {
    if (!window.cancelAnimationFrame) window.cancelAnimationFrame = window.webkitCancelAnimationFrame;
    window.cancelAnimationFrame(this.rafID);
    this.oscillatorNode.stop();
    this.oscillatorNode.disconnect();
    this.oscillatorNode = null;
    this.teardownAnalyzer();
    this.micSourceEnabled = false;
    if (this.params.onMicrophoneStateChanged) this.params.onMicrophoneStateChanged(this.micSourceEnabled);
  }

  setupAnalyzer(sourceNode) {
    this.lowPassFilter = this.audioContext.createBiquadFilter();
    this.lowPassFilter.Q.value = 0;
    this.lowPassFilter.frequency.value = 1046.502; // C6
    this.lowPassFilter.type = 'lowpass';

    this.highPassFilter = this.audioContext.createBiquadFilter();
    this.highPassFilter.Q.value = 0;
    this.highPassFilter.frequency.value = 130; // C3
    this.highPassFilter.type = 'highpass';

    this.gainNode = this.audioContext.createGain();
    this.gainNode.gain.value = 1.5; // Set mic volume to 150% by default

    this.analyser = this.audioContext.createAnalyser();
    this.analyser.fftSize = this.config.fftSize;
    this.dataArray = new Float32Array(this.analyser.frequencyBinCount);
    this.freqArray = new Uint8Array(this.analyser.frequencyBinCount);

    sourceNode.connect(this.lowPassFilter);
    this.lowPassFilter.connect(this.highPassFilter);
    this.highPassFilter.connect(this.gainNode);
    this.gainNode.connect(this.analyser);
    this.updatePitch();
  }

  teardownAnalyzer() {
    this.analyser && this.analyser.disconnect();
    this.analyser = null;
    this.highPassFilter.disconnect();
    this.highPassFilter = null;
    this.lowPassFilter.disconnect();
    this.lowPassFilter = null;
    this.gainNode.disconnect();
    this.gainNode = null;
  }

  updatePitch = () => {
    if (this.params.debug) {
      const currentTimestamp = parseInt(new Date().getTime() / 1000);
      if (this.lastFrameTime === null || currentTimestamp !== this.lastFrameTime) {
        console.log('FPS:', this.currentFrameCount, currentTimestamp, this.lastFrameTime);
        this.framePerSecondsCount = this.currentFrameCount;
        this.currentFrameCount = 0;
        this.lastFrameTime = currentTimestamp;
      }
      this.currentFrameCount++;
    }
    this.analyser.getFloatTimeDomainData(this.dataArray);
    this.analyser.getByteFrequencyData(this.freqArray);

    let detectedPitch = -1;
    if (signalAboveRMS(this.dataArray, this.config.rmsReference)) {
      // switch per algoritmo?
      //detectedPitch = autoCorrelate(this.dataArray, this.audioContext.sampleRate, this.config.correlationOutValue);
      detectedPitch = Yin_pitchEstimation(this.dataArray, this.audioContext.sampleRate);
    }

    if (!window.requestAnimationFrame) window.requestAnimationFrame = window.webkitRequestAnimationFrame;
    this.rafID = window.requestAnimationFrame(this.updatePitch);
    let newNote = null;
    if (detectedPitch > 0) {
      newNote = noteFromFrequency(detectedPitch);
    } else if (detectedPitch === -1) {
      //console.log('silenzio');
    } else if (detectedPitch === -2) console.log('nota non riconosciuta');

    if (this.params.onWaveChanged) this.params.onWaveChanged(newNote);

    // se le due note sono comparabili, allora aggiorno il timestamp
    if (this.lastNote !== null && newNote !== null && newNote.equals(this.lastNote)) {
      console.log('STESSA NOTA, non mando');
      this.lastNote.duration = newNote.timestamp - this.lastNote.timestamp;
    } else {
      // se lastNote è vuota e newNote c'è, non mando lastNote, salvo newNote
      // se lastNote è vuota e newNote non c'è... non mando lastNote, lastNote rimane null.
      // se lastNote c'è e newNote no, mando lastNote, metto lastNote a null
      // se lastNote c'è e newNote c'è mando lastNote e salvo newNote

      if (this.lastNote !== null) {
        console.log('mando nota:', this.lastNote, 'nuova nota', newNote);
        if (this.params.onNoteChanged) this.params.onNoteChanged(Object.assign({}, this.lastNote));
      }
      this.lastNote = newNote;
    }
  };
}

// Riferimenti:
// FFT - https://en.wikipedia.org/wiki/Fast_Fourier_transform

//let MIN_SAMPLES = 0; // will be initialized when AudioContext is created.

function signalAboveRMS(buf, rmsThreshold) {
  const RMS_THRESHOLD = rmsThreshold ? rmsThreshold : 0.01;
  let rms = 0;
  for (let i = 0; i < buf.length; i++) {
    const val = buf[i];
    rms += val * val;
  }
  rms = Math.sqrt(rms / buf.length);
  return rms >= RMS_THRESHOLD;
}

// function autoCorrelate(buf, sampleRate, acThreshold) {
//   const SIZE = buf.length;
//   const MAX_SAMPLES = Math.floor(SIZE / 2);
//   const correlations = new Array(MAX_SAMPLES);
//   let best_offset = -1;
//   let best_correlation = 0;
//   let foundGoodCorrelation = false;
//
//   let lastCorrelation = 1;
//   for (let offset = MIN_SAMPLES; offset < MAX_SAMPLES; offset++) {
//     let correlation = 0;
//
//     for (let i = 0; i < MAX_SAMPLES; i++) {
//       correlation += Math.abs(buf[i] - buf[i + offset]);
//     }
//     correlation = 1 - correlation / MAX_SAMPLES;
//     correlations[offset] = correlation; // store it, for the tweaking we need to do below.
//     if (correlation > acThreshold && correlation > lastCorrelation) {
//       foundGoodCorrelation = true;
//       if (correlation > best_correlation) {
//         best_correlation = correlation;
//         best_offset = offset;
//       }
//     } else if (foundGoodCorrelation) {
//       // short-circuit - we found a good correlation, then a bad one, so we'd just be seeing copies from here.
//       // Now we need to tweak the offset - by interpolating between the values to the left and right of the
//       // best offset, and shifting it a bit.  This is complex, and HACKY in this code (happy to take PRs!) -
//       // we need to do a curve fit on correlations[] around best_offset in order to better determine precise
//       // (anti-aliased) offset.
//
//       // we know best_offset >=1,
//       // since foundGoodCorrelation cannot go to true until the second pass (offset=1), and
//       // we can't drop into this clause until the following pass (else if).
//       const shift = (correlations[best_offset + 1] - correlations[best_offset - 1]) / correlations[best_offset];
//       return sampleRate / (best_offset + 8 * shift);
//     }
//     lastCorrelation = correlation;
//   }
//   if (best_correlation > 0.01) {
//     // console.log("f = " + sampleRate/best_offset + "Hz (rms: " + rms + " confidence: " + best_correlation + ")")
//     return sampleRate / best_offset;
//   }
//   // correlation not found, pitch not recognized
//   return -2;
//   //	var best_frequency = sampleRate/best_offset;
// }

/*
   Yin Pitch Tracking Algorithm implemented by Alejandro Pérez (2014)
   (http://recherche.ircam.fr/equipes/pcm/cheveign/ps/2002_JASA_YIN_proof.pdf)
*/

// PARAMETERS
const yinThreshold = 0.15; // allowed uncertainty (e.g 0.05 will return a pitch with ~95% probability)
//let yinProbability = 0.0; // READONLY: contains the certainty of the note found as a decimal (i.e 0.3 is 30%)

function Yin_pitchEstimation(inputBuffer, sampleRate) {
  const yinBuffer = new Float32Array(Math.floor(inputBuffer.length / 2));
  yinBuffer[0] = 1;
  let runningSum = 0;
  let pitchInHz = 0.0;
  let foundTau = false;
  let minTauValue;
  let minTau = 0;

  for (let tau = 1; tau < Math.floor(inputBuffer.length / 2); tau++) {
    // Step 1: Calculates the squared difference of the signal with a shifted version of itself.
    yinBuffer[tau] = 0;
    for (let i = 0; i < Math.floor(inputBuffer.length / 2); i++) {
      yinBuffer[tau] += Math.pow((inputBuffer[i] - 128) / 128 - (inputBuffer[i + tau] - 128) / 128, 2);
    }
    // Step 2: Calculate the cumulative mean on the normalised difference calculated in step 1.
    runningSum += yinBuffer[tau];
    yinBuffer[tau] = yinBuffer[tau] * (tau / runningSum);

    // Step 3: Check if the current normalised cumulative mean is over the threshold.
    if (tau > 1) {
      if (foundTau) {
        if (yinBuffer[tau] < minTauValue) {
          minTauValue = yinBuffer[tau];
          minTau = tau;
        } else break;
      } else if (yinBuffer[tau] < yinThreshold) {
        foundTau = true;
        minTau = tau;
        minTauValue = yinBuffer[tau];
      }
    }
  }

  if (minTau === 0) {
    //yinProbability = 0;
    pitchInHz = 0.0;
  } else {
    // Step 4: Interpolate the shift value (tau) to improve the pitch estimate.
    minTau +=
      (yinBuffer[minTau + 1] - yinBuffer[minTau - 1]) /
      (2 * (2 * yinBuffer[minTau] - yinBuffer[minTau - 1] - yinBuffer[minTau + 1]));
    pitchInHz = sampleRate / minTau;
    //yinProbability = 1 - minTauValue;
  }

  return pitchInHz;
}

export default PitchDetectionController;
